/**
 * Shared error reporter — captures unhandled frontend errors and forwards them
 * to the backend via POST /rpc/ReportClientError.
 *
 * Only fires when an auth token is present (auth-required endpoint).
 * Client-side throttling: max 5 unique errors per 60-second window.
 */

const MAX_ERRORS_PER_WINDOW = 5;
const WINDOW_MS = 60_000;

let getAuthTokenFn: (() => string | null) | null = null;
let getApiBaseUrlFn: (() => string) | null = null;
let currentUserId: string | null = null;

// Dedup map: error key → timestamp of first report in current window.
const recentErrors = new Map<string, number>();
let errorCount = 0;
let windowStart = 0;

/** Derive a dedup key from an error message and its first stack frame. */
function dedupeKey(message: string, stack?: string): string {
  const firstFrame = stack?.split('\n').find(line => line.trim().startsWith('at '))?.trim() ?? '';
  return `${message}|||${firstFrame}`;
}

/** Check if this error should be reported (throttle + dedup). */
function shouldReport(key: string): boolean {
  const now = Date.now();

  // Reset window if expired.
  if (now - windowStart > WINDOW_MS) {
    recentErrors.clear();
    errorCount = 0;
    windowStart = now;
  }

  // Already reported this exact error in this window.
  if (recentErrors.has(key)) {
    return false;
  }

  // Rate limit reached.
  if (errorCount >= MAX_ERRORS_PER_WINDOW) {
    return false;
  }

  recentErrors.set(key, now);
  errorCount++;
  return true;
}

/** Fire-and-forget: send error report to backend. */
function sendReport(message: string, stack?: string): void {
  const key = dedupeKey(message, stack);
  if (!shouldReport(key)) return;

  const token = getAuthTokenFn?.();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const payload = JSON.stringify({
    message,
    stack: stack ?? '',
    url: window.location.href,
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  });

  const baseUrl = getApiBaseUrlFn?.() ?? '';
  try {
    fetch(`${baseUrl}/rpc/ReportClientError`, {
      method: 'POST',
      headers,
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Silently ignore — error reporting must never cause more errors.
    });
  } catch {
    // Silently ignore.
  }
}

/**
 * Manually report an error to the backend.
 * Use this for caught errors that would otherwise not be reported
 * (e.g., import failures, React ErrorBoundary catches, network errors handled in try/catch blocks).
 */
export function reportError(error: Error | string, componentStack?: string): void {
  let message: string;
  let stack: string | undefined;

  if (typeof error === 'string') {
    message = error;
    stack = componentStack;
  } else {
    message = error.message;
    stack = error.stack;
    if (componentStack) {
      stack = stack ? `${stack}\n\nComponent Stack:\n${componentStack}` : componentStack;
    }
  }

  sendReport(message, stack);
}

/**
 * Initialize the error reporter. Must be called once in main.tsx before React renders.
 * @param config.getAuthToken Function that returns the current auth token, or null.
 * @param config.getApiBaseUrl Optional function that returns the API base URL.
 */
export function initErrorReporter(config: {
  getAuthToken: () => string | null;
  getApiBaseUrl?: () => string;
}): void {
  getAuthTokenFn = config.getAuthToken;
  getApiBaseUrlFn = config.getApiBaseUrl ?? null;

  // Catch unhandled JS exceptions.
  window.onerror = (event, source, lineno, colno, error) => {
    const message = error?.message ?? String(event);
    let stack = error?.stack;
    if (!stack && source) {
      stack = `at ${source}:${lineno}:${colno}`;
    }
    sendReport(message, stack);
  };

  // Catch unhandled promise rejections.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      sendReport(reason.message, reason.stack);
    } else {
      sendReport(String(reason));
    }
  });

  // Catch unhandled errors in capture phase (DOM listeners, resource loading failures)
  window.addEventListener(
    'error',
    (event: Event | ErrorEvent) => {
      // If it's a JS runtime error with an Error object or message, capture and send report
      if ('error' in event && event.error) {
        sendReport(event.error.message || String((event as ErrorEvent).message), event.error.stack);
        return;
      }
      if ('message' in event && (event as ErrorEvent).message) {
        const errEvent = event as ErrorEvent;
        const stack = errEvent.filename ? `at ${errEvent.filename}:${errEvent.lineno}:${errEvent.colno}` : undefined;
        sendReport(errEvent.message, stack);
        return;
      }

      // Check if it's a resource element loading failure
      const target = event.target as HTMLElement | null;
      if (target && target !== (window as unknown)) {
        const tagName = target.tagName ? target.tagName.toLowerCase() : '';
        if (tagName === 'script' || tagName === 'link' || tagName === 'img' || tagName === 'video' || tagName === 'audio') {
          const src = (target as HTMLScriptElement | HTMLImageElement).src || (target as HTMLLinkElement).href || '';
          sendReport(`Resource load error: <${tagName}> failed to load ${src}`);
        }
      }
    },
    true
  );
}

/**
 * Update user metadata for error reports.
 * Called from AuthContext when auth state changes.
 */
export function setErrorReporterUser(user: { id: string } | null): void {
  currentUserId = user?.id ?? null;
}

